import type { EffectRef, Signal } from '@angular/core';
import { effect, signal, untracked } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { form, readonly } from '@angular/forms/signals';

import { createFormTransaction } from '../plugins/form-transaction';
import { deepPatch } from './deep-patch';

/**
 * Minimal shape for entities that pre-date the versioned-entity contract. Unlike
 * {@link BaseEntity} we only require `id` — there is no `parentId`,
 * `entityType`, `entitySubType`, or `version`. This matches widgets stored in
 * the host app's existing page structure (e.g. `WebLegacyWidgetEntity`), letting the
 * signal form drive the widget JSON directly without an adapter step.
 *
 * `data` is optional: every widget carries one, but flat entities such as a
 * default section keep their fields on the envelope. Those pass their own
 * {@link BaseLegacyEntityFormOptions.isReady}.
 */
type BaseLegacyEntity = {
    id: string;
    data?: any;
};

export type BaseLegacyEntityForm<T extends BaseLegacyEntity> = {
    _raw: FieldTree<T>;
    syncFromEntity: (entity: T) => void;
    destroy: () => void;
    /**
     * Ask the host to remove this entity. The optional `payload` is forwarded
     * verbatim to {@link BaseLegacyEntityFormOptions.onRemove}; the form itself
     * never inspects it — placement context the form doesn't own (index, cell)
     * travels through here from the caller. The form is *not* destroyed by this
     * call; the host's normal teardown path (ref-count release) does that.
     */
    remove: (payload?: unknown) => void;
    /** Ask the host to copy this entity; see {@link BaseLegacyEntityForm.remove} for the payload contract. */
    copy: (payload?: unknown) => void;
    inTransaction: Signal<boolean>;
    beginTransaction: () => void;
    commitTransaction: () => void;
    rollbackTransaction: () => void;
    transact: (fn: () => void) => void;
    /**
     * Same semantics as {@link BaseLegacyEntityForm.transact}, but the
     * `onUpdate` emission produced by the commit carries `{ skipHistory: true }`
     * — hosts use it to persist the change without recording a history entry.
     * Caveat: synchronous writes coalesce into one `onUpdate` per tick, so any
     * plain writes landing in the same tick are tagged skip-history too.
     */
    transactWithoutHistory: (fn: () => void) => void;
    value: () => T;
    setValue: (value: T) => void;
    /**
     * The `id` field as a {@link FieldTree} node — call it (`form.id()`) to get
     * the `FieldState`, then `.value()`, `.touched()`, `.dirty()`, `.errors()`.
     * Each field exposed by an entity form follows this same pattern.
     */
    id: FieldTree<string>;
};

/**
 * Marks the infrastructure fields shared by every legacy widget entity
 * (`id`, `name`, `columnId`) as `readonly` on the schema path. The editor never
 * lets the user mutate these directly; surfacing them as read-only paths blocks
 * accidental writes from `setValue` callers and gives the schema-validation
 * layer a single place to enforce that. Called from each legacy widget factory's
 * schema configurator.
 */
export function configureCommonReadonly(schemaPath: any): void {
    readonly(schemaPath.id);
    readonly(schemaPath.name);
    readonly(schemaPath.columnId);
}

export interface BaseLegacyEntityFormOptions<T extends BaseLegacyEntity> {
    entity?: Signal<any>;
    /**
     * Synchronous snapshot of the entity at form-construction time, used by
     * variant-aware dispatchers (e.g. `createVideoForm`) to pick the right
     * per-variant factory before the `entity` signal has populated. Callers
     * that pass `entity` for live syncing can pass `initialData` here too —
     * the factory only inspects it at construction, never reactively.
     */
    initialData?: T;
    onUpdate?: (value: T, meta: { skipHistory: boolean }) => void;
    /**
     * Guards the first `onUpdate` emission: the model is seeded empty and only
     * becomes real once `syncFromEntity` (or the `entity` effect) has run.
     * Defaults to "has a `data` bag", which is what every widget carries.
     * Entities whose payload is flat — a default section has no `data` at all —
     * pass their own check.
     */
    isReady?: (value: T) => boolean;
    onCreate?: (ctx: { form: BaseLegacyEntityForm<T> }) => void;
    onDestroy?: () => void;
    onRemove?: (payload?: unknown) => void;
    onCopy?: (payload?: unknown) => void;
}

/**
 * Sibling of {@link createBaseEntityForm} for the legacy entity contract.
 * Implementation is intentionally a near-duplicate so changes to one don't
 * accidentally couple unrelated entity kinds together — see the comment on
 * {@link BaseLegacyEntity} for why the contract differs.
 */
export function createBaseLegacyEntityForm<T extends BaseLegacyEntity>(
    configureSchema: (schemaPath: any) => void,
    options?: BaseLegacyEntityFormOptions<T>
) {
    // Seed the model with the known BaseLegacyEntity keys (`id`, `data`) so the
    // `form()` proxy materialises `_raw.id` and `_raw.data` FieldTree nodes —
    // subclass factories rely on `_raw.<key>` being callable for every key they
    // expose. `syncFromEntity` (or the `entity` effect below) replaces this with
    // real data immediately.
    const model = signal<T>({ id: '', data: null } as any);
    let entitySyncVersion = 0;
    let lastSeenSyncVersion = 0;
    let skipHistoryOnNextUpdate = false;

    const _raw = form(model, configureSchema as any);

    const _tx = createFormTransaction(model);

    const effectRefs: EffectRef[] = [];

    if (options?.entity) {
        effectRefs.push(
            effect(() => {
                const entityValue = options.entity!();
                untracked(() => {
                    const prev = model();
                    model.update((current) => deepPatch(current, entityValue));
                    if (model() !== prev) {
                        entitySyncVersion++;
                    }
                });
            })
        );
    }

    if (options?.onUpdate) {
        effectRefs.push(
            effect(() => {
                const value = model();
                const inTx = _tx.inTransaction();

                if (entitySyncVersion !== lastSeenSyncVersion) {
                    lastSeenSyncVersion = entitySyncVersion;
                    return;
                }
                if (inTx) {
                    return;
                }
                if (!(options.isReady ? options.isReady(value) : value.data)) {
                    return;
                }

                untracked(() => {
                    const skipHistory = skipHistoryOnNextUpdate;
                    skipHistoryOnNextUpdate = false;
                    // A copy: the host's store freezes what it is given (immer reducers), and signal forms still
                    // has to tag the model's array items — and anything they share, e.g. a cached CRM pipeline
                    // another form picks next (#75636).
                    options.onUpdate!(structuredClone(value), { skipHistory });
                });
            })
        );
    }

    const result: BaseLegacyEntityForm<T> = {
        _raw,

        syncFromEntity: (entity: T) => {
            const prev = model();
            model.update((current) => deepPatch(current, entity));
            if (model() !== prev) {
                entitySyncVersion++;
            }
        },

        destroy: () => {
            options?.onDestroy?.();
            for (const ref of effectRefs) {
                ref.destroy();
            }
        },

        remove: (payload?: unknown) => {
            options?.onRemove?.(payload);
        },

        copy: (payload?: unknown) => {
            options?.onCopy?.(payload);
        },

        inTransaction: _tx.inTransaction,
        beginTransaction: () => _tx.beginTransaction(),
        commitTransaction: () => _tx.commitTransaction(),
        rollbackTransaction: () => {
            // Only suppress the rollback's reverting emission (via the version
            // guard) when there is actually an open transaction to roll back.
            // Bumping unconditionally would arm the guard against the *next*
            // genuine user edit and silently drop it. See `_tx.rollbackTransaction`,
            // which no-ops (warns) when no transaction is active.
            if (_tx.inTransaction()) {
                entitySyncVersion++;
            }
            _tx.rollbackTransaction();
        },
        transact: (fn: () => void) => _tx.transact(fn),
        transactWithoutHistory: (fn: () => void) => {
            const before = untracked(model);
            // Only the outermost frame produces the emission this flag is meant to
            // tag. Nested inside another transaction (a panel session, a wrapping
            // `transact`) the emission belongs to that caller's user action, and
            // arming the flag would strip *its* history entry instead.
            const ownsEmission = !_tx.inTransaction();
            try {
                _tx.transact(fn);
            } finally {
                // Arm only when the model actually changed, so the flag can't
                // leak onto an unrelated later update. Rollback restores a
                // cloned snapshot (new ref), so a reverting emission is also
                // tagged skip-history.
                if (ownsEmission && untracked(model) !== before) {
                    skipHistoryOnNextUpdate = true;
                }
            }
        },

        value: () => _raw().value() as T,
        setValue: (value: T) => _raw().value.set(value as any),

        // FieldTree reference, exposed via a getter so the path is materialised
        // lazily — by the time a consumer reads `form.id`, `syncFromEntity` has
        // populated the model. Call `form.id()` to get the FieldState, then
        // `.value()` / `.touched()` / `.dirty()` / `.errors()` as needed.
        get id() {
            return (_raw as unknown as { id: FieldTree<string> }).id;
        },
    };

    options?.onCreate?.({ form: result });

    return result;
}
